/**
 * Add Block Button Component
 * Unobtrusive button for adding blocks between existing blocks or at the end
 */

import { useState, useRef, useLayoutEffect, useCallback } from 'react';
import { useTheme } from '@course-authoring/shared-types';
import { BlockTypeChooser, type BlockTypeOption } from './BlockTypeChooser';

interface AddBlockButtonProps {
  onAddBlock: (type: BlockTypeOption) => void;
  variant?: 'inline' | 'bottom';
  excludeBlockTypes?: string[];
}

export function AddBlockButton({ onAddBlock, variant = 'bottom', excludeBlockTypes }: AddBlockButtonProps) {
  const { tokens } = useTheme();
  const [showChooser, setShowChooser] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [chooserPos, setChooserPos] = useState<{ x: number; y: number } | undefined>(undefined);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleSelect = (type: BlockTypeOption) => {
    onAddBlock(type);
    setShowChooser(false);
  };

  const openChooser = useCallback(() => {
    if (!buttonRef.current) {
      setShowChooser(true);
      return;
    }
    const rect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const margin = 8;

    if (variant === 'bottom') {
      // Prefer opening above the button
      // Estimate menu height (~440px for the full menu, capped to available space)
      const estimatedMenuHeight = 440;
      const spaceAbove = rect.top;
      const spaceBelow = viewportHeight - rect.bottom;

      if (spaceAbove >= estimatedMenuHeight || spaceAbove > spaceBelow) {
        // Open above: position so the menu bottom aligns above the button
        const y = Math.max(margin, rect.top - estimatedMenuHeight);
        setChooserPos({ x: rect.left + rect.width / 2 - 140, y });
      } else {
        // Open below
        setChooserPos({ x: rect.left + rect.width / 2 - 140, y: rect.bottom + 8 });
      }
    } else {
      // Inline: prefer opening below
      const spaceBelow = viewportHeight - rect.bottom - margin;
      if (spaceBelow >= 200) {
        setChooserPos({ x: rect.left + rect.width / 2 - 140, y: rect.bottom + 8 });
      } else {
        // Open above
        const estimatedMenuHeight = 440;
        const y = Math.max(margin, rect.top - estimatedMenuHeight);
        setChooserPos({ x: rect.left + rect.width / 2 - 140, y });
      }
    }
    setShowChooser(true);
  }, [variant]);

  const styles: Record<string, React.CSSProperties> = {
    inlineContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '-10px 0',
      minHeight: 0,
      position: 'relative',
      zIndex: 2,
    },
    inlineButtonWrapper: {
      position: 'relative',
      transition: `opacity ${tokens.ui.transitionDefault}`,
    },
    inlineButton: {
      width: '28px',
      height: '28px',
      padding: 0,
      fontSize: '18px',
      fontWeight: 500,
      color: tokens.text.muted,
      backgroundColor: tokens.backgrounds.app,
      border: `1px solid ${tokens.borders.default}`,
      borderRadius: tokens.radius.full,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: tokens.shadow.sm,
      fontFamily: tokens.typography.fontFamily,
    },
    bottomContainer: {
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
    },
    bottomButton: {
      padding: `${tokens.spacing.md} ${tokens.spacing.xl}`,
      fontSize: tokens.navigation.itemFontSize,
      color: tokens.text.muted,
      backgroundColor: tokens.backgrounds.app,
      border: `1px dashed ${tokens.borders.default}`,
      borderRadius: tokens.radius.sm,
      cursor: 'pointer',
      transition: `border-color ${tokens.ui.transitionDefault}, color ${tokens.ui.transitionDefault}`,
      fontFamily: tokens.typography.fontFamily,
      boxShadow: tokens.shadow.sm,
    },
  };

  if (variant === 'inline') {
    return (
      <div
        style={styles.inlineContainer}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => !showChooser && setIsHovered(false)}
      >
        <div
          style={{
            ...styles.inlineButtonWrapper,
            opacity: isHovered || showChooser ? 1 : 0,
          }}
        >
          <button
            ref={buttonRef}
            onClick={openChooser}
            style={styles.inlineButton}
            aria-label="Add content block here"
          >
            +
          </button>
          {showChooser && (
            <BlockTypeChooser
              onSelect={handleSelect}
              onClose={() => {
                setShowChooser(false);
                setIsHovered(false);
              }}
              position={chooserPos}
              excludeBlockTypes={excludeBlockTypes}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.bottomContainer}>
      <button
        ref={buttonRef}
        onClick={openChooser}
        style={styles.bottomButton}
        aria-label="Add content block"
      >
        + Add content
      </button>
      {showChooser && (
        <BlockTypeChooser
          onSelect={handleSelect}
          onClose={() => setShowChooser(false)}
          position={chooserPos}
          excludeBlockTypes={excludeBlockTypes}
        />
      )}
    </div>
  );
}

